import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { fetchUserInfo } from '../../utils';
import Base from '../Base';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import companyFallback from '../../assets/images/fallback/company.png';
import '../user/PublicProfile.css';
import './EventList.css';

// The search box filters the own list and the other event's alike.
const matches = (q) => (c) => (c.name?.toLowerCase() || '').includes(q);

const Companies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUserInfo('/companies', setUserInfo, setLoading, setError, ['selectedEventId']);
  }, []);

  const filteredCompanies = useMemo(() => {
    if (!userInfo?.companies) return null;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return userInfo.companies;
    return userInfo.companies.filter(matches(q));
  }, [userInfo, searchQuery]);

  // The other event on the same day at the same venue (HT and PLG Dubai) — for information only.
  // A company playing both events is already in the list above; the API leaves it out here.
  const siblingEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (userInfo?.sibling_events || [])
      .map((s) => ({ ...s, items: q ? s.items.filter(matches(q)) : s.items }))
      .filter((s) => s.items.length > 0);
  }, [userInfo, searchQuery]);

  const renderCard = (c) => (
    <Link
      key={c.id}
      to={`/public-company/${c.id}`}
      className="el-company-card"
    >
      <div className="el-company-logo-tile">
        <img src={c.photo_url || companyFallback} alt="" className="el-company-logo" />
        {typeof c.team_count === 'number' && c.team_count > 0 && (
          <span className="el-company-team-pill">
            <Icon name="users-alt" pack="sr" theme="light" />
            {c.team_count}
          </span>
        )}
      </div>
      <div className="el-company-meta">
        <div className="el-company-name">{c.name}</div>
        {typeof c.team_count === 'number' && (
          <div className="el-company-sub">
            {c.team_count} {c.team_count === 1 ? t('team') : t('teams')}
          </div>
        )}
      </div>
    </Link>
  );

  return (
    <Base activeNav="home">
      <div className="pp-page">
        <div className="el-topbar">
          <button type="button" className="el-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <Icon name="angle-left" />
          </button>
          <h1 className="el-page-title">{t('companies')}</h1>
        </div>

        <div className="el-search-bar">
          <Icon name="search" className="el-search-icon" />
          <input
            type="text"
            className="el-search-input"
            placeholder={t('searchCompanies')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="el-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
            >
              <Icon name="cross" />
            </button>
          )}
        </div>

        {filteredCompanies && filteredCompanies.length > 0 ? (
          <div className="el-company-grid">
            {filteredCompanies.map(renderCard)}
          </div>
        ) : (
          <div className="el-empty">
            <NotFound translationKey="noCompaniesFound" loading={loading} />
          </div>
        )}

        {siblingEvents.map((s) => (
          <section key={s.event_id}>
            <h2 className="el-sibling-head">
              {s.logo && <img src={s.logo} alt="" className="el-sibling-logo" />}
              {t('siblingListTitle', { event: s.event_name })}
            </h2>
            <div className="el-company-grid el-sibling-grid">
              {s.items.map(renderCard)}
            </div>
          </section>
        ))}
      </div>
    </Base>
  );
};

export default Companies;
